import { supabase } from "./supabase";
import { decodeBrandProduct } from "./brand-data";

export type DemandShowcaseProduct = {
  product: string;
  memberCount: number;
};

export type DemandShowcase = {
  products: DemandShowcaseProduct[];
  weekStartedOn: string;
  autoUpdate: boolean;
  updatedAt: string | null;
};

type DemandShowcaseRow = {
  products: unknown;
  week_started_on: string;
  auto_update: boolean;
  updated_at: string | null;
};

function normalizeProducts(value: unknown): DemandShowcaseProduct[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const product = String((item as { product?: unknown }).product ?? "").trim();
    const memberCount = Number((item as { memberCount?: unknown }).memberCount ?? 0);
    return product && !decodeBrandProduct(product) ? [{ product, memberCount: Number.isFinite(memberCount) ? memberCount : 0 }] : [];
  });
}

function normalizeDemandShowcase(row: DemandShowcaseRow): DemandShowcase {
  return {
    products: normalizeProducts(row.products),
    weekStartedOn: row.week_started_on,
    autoUpdate: row.auto_update,
    updatedAt: row.updated_at,
  };
}

export async function readWeeklyDemandShowcase(): Promise<DemandShowcase | null> {
  const { data, error } = await supabase.rpc("refresh_weekly_demand_showcase", { p_force: false }).maybeSingle();
  if (error) throw error;
  return data ? normalizeDemandShowcase(data as DemandShowcaseRow) : null;
}

export async function readAdminDemandShowcase(): Promise<DemandShowcase | null> {
  const { data, error } = await supabase
    .from("site_demand_showcase")
    .select("products,week_started_on,auto_update,updated_at")
    .eq("id", "weekly")
    .maybeSingle();
  if (error) throw error;
  return data ? normalizeDemandShowcase(data as DemandShowcaseRow) : null;
}

export async function refreshAdminDemandShowcase(): Promise<DemandShowcase> {
  const { data, error } = await supabase.rpc("refresh_weekly_demand_showcase", { p_force: true }).single();
  if (error) throw error;
  return normalizeDemandShowcase(data as DemandShowcaseRow);
}

export async function setDemandShowcaseAutoUpdate(autoUpdate: boolean): Promise<DemandShowcase> {
  const { data, error } = await supabase
    .from("site_demand_showcase")
    .update({ auto_update: autoUpdate, updated_at: new Date().toISOString() })
    .eq("id", "weekly")
    .select("products,week_started_on,auto_update,updated_at")
    .single();
  if (error) throw error;
  return normalizeDemandShowcase(data as DemandShowcaseRow);
}
